import React, { forwardRef, useContext, useEffect, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as yup from "yup";
import AdxInput from "components/custom/AdxInput";
import Box from "@mui/system/Box";
import InputAdornment from "@mui/material/InputAdornment";
import AdxDatePicker from "components/custom/AdxDatePicker";
import { parse, set } from "date-fns";
import AdxDropzoneImagePdf from "components/reusable/AdxDropzoneImagePdf";
import AdxErrorMessage from "components/custom/AdxErrorMessage";
import { ExpenseContext } from "../context/ExpenseContext";

const MileageExpenseForm = forwardRef((props, ref) => {
  const {
    category = "",
    disabled = true,
    mileageRate,
    categoryAllocation,
    expenseMutate,
    expenseData,
    reportData,
  } = props;

  // reset today's time to 12am
  const { start_date, end_date } = reportData;
  const today = set(new Date(), {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  // form validation schema
  const validationSchema = yup.object({
    claim_expense_category_id: yup.string().required("Category is required"),
    description: yup.string().required("Description is required"),
    date: yup
      .date()
      .required("Travel date is required")
      .max(today, "Your travel cannot be in the future"),
    distance: yup
      .number()
      .required("Travel distant is required")
      .positive("Invalid travel distant"),
    proof_link: yup
      .string()
      .trim()
      .max(2048, "Proof link must be 2048 characters or less")
      .url("Proof link must be a valid URL")
      .nullable()
      .notRequired(),
    file: yup.mixed().required("Screenshot is required"),
  });

  // set category id when dropdown change
  useEffect(() => {
    ref.current.setFieldValue("claim_expense_category_id", category);
  }, [category]);

  // render component when distance field is change and instantaneously update the calculated amount field
  const [calculatedAmountState, setCalculatedState] = useState(
    expenseData?.amount || ""
  );
  useEffect(() => {
    ref.current.setFieldValue(
      "calculatedAmount",
      (() => {
        if (
          calculatedAmountState > categoryAllocation &&
          categoryAllocation !== null
        ) {
          return categoryAllocation;
        } else if (ref.current.values.distance === "") {
          return 0.0;
        } else {
          return parseFloat(calculatedAmountState).toFixed(2);
        }
      })()
    );
  }, [calculatedAmountState]);

  // update the context every 100 miliseconds
  const { savedExpenseSession, setExpenseSession } = useContext(ExpenseContext);
  const [time, setTime] = useState(Date.now());

  useEffect(() => {
    if (!disabled && !expenseData) {
      const interval = setInterval(() => setTime(Date.now()), 100);
      return () => {
        clearInterval(interval);
      };
    }
  }, [disabled, category]);
  useEffect(() => {
    if (!disabled && !expenseData) {
      setExpenseSession({ ...ref.current.values });
    }
  }, [time]);

  return (
    <Formik
      innerRef={ref}
      initialValues={{
        claim_expense_category_id: category,
        description: expenseData?.description ? expenseData?.description : "",
        date: expenseData?.date ? expenseData?.date : "",
        distance: expenseData?.distance ? expenseData?.distance : "",
        calculatedAmount: expenseData?.amount ? expenseData?.amount : "0.00",
        proof_link: expenseData?.proof_link ? expenseData?.proof_link : "",
        file: expenseData?.file ? expenseData?.file?.name : "",
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const { file } = values;
        const newValues = {
          ...values,
          proof_link: values.proof_link?.trim() || null,
          attachment: file.name || file,
        };
        expenseMutate(newValues);
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setValues,
        setFieldValue,
        setFieldError,
        setFieldTouched,
      }) => {
        const handleDistanceChange = (e) => {
          handleChange(e);
          setCalculatedState(e.target.value * mileageRate);
        };
        return (
          <Form>
            <Box width={1} display="flex" flexDirection="column" rowGap={1}>
              <Field
                disabled={disabled}
                name="description"
                as={AdxInput}
                multiline
                minRows={3}
                sxInput={{ padding: 2 }}
                charLimit={120}
                sx={{ width: 1 }}
                label="Description"
                onChange={handleChange}
                onBlur={handleBlur}
                errorMessage={
                  <ErrorMessage
                    name="description"
                    component={AdxErrorMessage}
                  />
                }
                isErrorShown={errors.description && touched.description}
              />

              <Field
                disabled={disabled}
                name="distance"
                as={AdxInput}
                label="Travel distance"
                subLabel={`Rate : RM ${parseFloat(mileageRate).toFixed(2)}/km`}
                type="number"
                endAdornment={
                  <InputAdornment position="end">km</InputAdornment>
                }
                inputProps={{
                  min: 0,
                }}
                onChange={handleDistanceChange}
                onBlur={handleBlur}
                errorMessage={
                  <ErrorMessage name="distance" component={AdxErrorMessage} />
                }
                isErrorShown={errors.distance && touched.distance}
                sx={{ mt: mileageRate ? 2 : null, mb: 2 }}
              />
              <Box display={{ xs: "block", lg: "flex" }} columnGap={3}>
                <Field
                  disabled={disabled}
                  name="calculatedAmount"
                  as={AdxInput}
                  label="Amount"
                  subLabel={
                    !disabled && categoryAllocation !== null
                      ? `Balance RM ${parseFloat(categoryAllocation).toFixed(
                          2
                        )}`
                      : null
                  }
                  type="number"
                  sx={{ width: { xs: 1, lg: 1 / 2 } }}
                  startAdornment={
                    <InputAdornment position="start">RM</InputAdornment>
                  }
                  readOnly
                  onClick={(e) => {
                    setFieldError(
                      "distance",
                      "The amount will be calculated based on the travel distant"
                    );
                    setFieldTouched("distance", true, false);
                  }}
                />
                <Field
                  disabled={disabled}
                  name="date"
                  as={AdxDatePicker}
                  sx={{
                    width: { xs: 1, lg: 1 / 2 },
                    marginTop: { xs: 3, lg: 0 },
                  }}
                  label="Travel date"
                  onChange={(e) => {
                    if (e === null) {
                      setFieldValue("date", "");
                    } else {
                      setFieldValue(
                        "date",
                        set(e, {
                          hours: 0,
                          minutes: 0,
                          seconds: 0,
                          milliseconds: 0,
                        })
                      );
                    }
                  }}
                  onBlur={handleBlur}
                  minDate={parse(start_date, "dd/MM/yyyy", new Date())}
                  maxDate={parse(end_date, "dd/MM/yyyy", new Date())}
                  errorMessage={
                    <ErrorMessage name="date" component={AdxErrorMessage} />
                  }
                  isErrorShown={errors.date && touched.date}
                />
              </Box>
              <Field
                disabled={disabled}
                name="proof_link"
                as={AdxInput}
                label="Proof link"
                subLabel="Optional"
                placeholder="Paste Google Maps or site-study link"
                charLimit={2048}
                onChange={handleChange}
                onBlur={handleBlur}
                errorMessage={
                  <ErrorMessage name="proof_link" component={AdxErrorMessage} />
                }
                isErrorShown={errors.proof_link && touched.proof_link}
              />
              <Field name="file" type="hidden" />

              <AdxDropzoneImagePdf
                label="Attachment"
                title="Upload receipt from computer"
                subtitle="or click here to submit claim"
                folder="claim_expenses"
                setFieldValue={setFieldValue}
                file={
                  savedExpenseSession.resumeStatus && savedExpenseSession?.file
                    ? {
                        url: savedExpenseSession?.file?.awsUrl,
                        name: savedExpenseSession?.file?.name,
                      }
                    : expenseData?.file
                }
                disabled={disabled}
                errorMessage={
                  <ErrorMessage name="file" component={AdxErrorMessage} />
                }
                isErrorShown={errors.file && touched.file}
                parentsDependencies={[savedExpenseSession.resumeStatus]}
              />

              <button type="submit" hidden>
                Submit
              </button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
});

export default MileageExpenseForm;
